/**
 * Translation bundles, discovered by file name. Each feature owns `features/<name>/i18n/<code>.json`
 * (namespace = feature name in camelCase); shared strings are `shared/i18n/<code>.json` (namespace "common")
 * and status labels `business/status/i18n/<code>.json` ("status").
 *
 * English is bundled (it is the default and the fallback). Other languages load when first selected.
 */
import type { Resource, ResourceLanguage } from 'i18next';

import { DEFAULT_LOCALE } from '@/shared/i18n';

type Bundle = Record<string, unknown>;

const english = import.meta.glob<Bundle>(['../features/*/i18n/en.json', '../shared/i18n/en.json', '../business/status/i18n/en.json'], { eager: true, import: 'default' });
const all = import.meta.glob<Bundle>(['../features/*/i18n/*.json', '../shared/i18n/*.json', '../business/status/i18n/*.json'], { import: 'default' });

const camel = (s: string) => s.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

/** "../features/banks-products/i18n/ar.json" -> { ns: "banksProducts", code: "ar" } */
export function parseBundlePath(path: string): { ns: string; code: string } {
  const code = /\/([^/]+)\.json$/.exec(path)?.[1] ?? '';
  const feature = /\/features\/([^/]+)\/i18n\//.exec(path)?.[1];
  if (feature) return { ns: camel(feature), code };
  if (path.includes('/business/status/')) return { ns: 'status', code };
  return { ns: 'common', code };
}

export const defaultResources: Resource = {
  [DEFAULT_LOCALE]: Object.fromEntries(Object.entries(english).map(([path, strings]) => [parseBundlePath(path).ns, strings])),
};

/** Every bundle file per language, for the coverage check. */
export const bundlePaths = Object.keys(all);

export async function loadLocale(code: string): Promise<ResourceLanguage> {
  const entries = Object.entries(all).filter(([path]) => parseBundlePath(path).code === code);
  const loaded = await Promise.all(entries.map(async ([path, load]) => [parseBundlePath(path).ns, await load()] as const));
  return Object.fromEntries(loaded);
}
